import React, { useState } from 'react';
import { Trash2, X } from 'lucide-react';

interface Precaution {
    id: string;
    description: string;
}

interface PrecautionSectionProps {
    precautions: Precaution[];
    setPrecautions: React.Dispatch<React.SetStateAction<Precaution[]>>;
}

const PrecautionSection: React.FC<PrecautionSectionProps> = ({ precautions, setPrecautions }) => {
    const [showPrecautionModal, setShowPrecautionModal] = useState(false);
    const [newPrecaution, setNewPrecaution] = useState({ description: '' });

    const addPrecaution = () => {
        if (newPrecaution.description) {
            setPrecautions([...precautions, {
                id: Math.floor(Math.random() * 1000000).toString(),
                description: newPrecaution.description
            }]);
            setNewPrecaution({ description: '' });
            setShowPrecautionModal(false);
        }
    };

    const removePrecaution = (id: string) => {
        setPrecautions(precautions.filter(p => p.id !== id));
    };

    return (
        <>
            <div className="mb-2 border-t pt-6">
                <div className="flex flex-col justify-start items-start gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-green-600">PRECAUTION STEPS</h3>
                    <p className="text-sm text-gray-600">Included Steps</p>
                </div>
                <button
                    onClick={() => setShowPrecautionModal(true)}
                    className="mb-4 px-4 py-2 text-green-600 border border-green-600 rounded-md hover:bg-green-50"
                >
                    Add Step
                </button>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {precautions.map((precaution) => (
                        <div key={precaution.id} className="border border-gray-200 rounded-lg p-4 relative">
                            <button
                                onClick={() => removePrecaution(precaution.id)}
                                className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                            >
                                <Trash2 size={16} />
                            </button>
                            <p className="text-xs text-gray-600">{precaution.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {showPrecautionModal && (
                <PrecautionModal
                    newPrecaution={newPrecaution}
                    setNewPrecaution={setNewPrecaution}
                    onAdd={addPrecaution}
                    onClose={() => setShowPrecautionModal(false)}
                />
            )}
        </>
    );
};

interface PrecautionModalProps {
    newPrecaution: { description: string };
    setNewPrecaution: React.Dispatch<React.SetStateAction<{ description: string }>>;
    onAdd: () => void;
    onClose: () => void;
}

const PrecautionModal: React.FC<PrecautionModalProps> = ({
    newPrecaution,
    setNewPrecaution,
    onAdd,
    onClose
}) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Add Precaution Step</h3>
                    <button onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                            placeholder="Enter description"
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            value={newPrecaution.description}
                            onChange={(e) => setNewPrecaution({ ...newPrecaution, description: e.target.value })}
                        />
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onAdd}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                            Add
                        </button>
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrecautionSection;